import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { AD_OFFERS, type AdOffer } from '@/lib/ads-campaigns';
import OfferCard from './components/OfferCard';
import SubscribeModal from './components/SubscribeModal';
import Reveal from './components/Reveal';

const adsHero =
  'https://readdy.ai/api/search-image?query=Premium%20advertising%20marketing%20abstract%20illustration%20with%20a%20glowing%20golden%20megaphone%20radiating%20light%20beams%20and%20floating%20rising%20bar%20charts%20brand%20banners%20and%20icons%20in%20warm%20amber%20orange%20and%20gold%20gradient%20tones%20on%20a%20deep%20dark%20charcoal%20background%20modern%20editorial%20style%20soft%20glow%20effects%20high%20detail%20professional%20composition&width=1600&height=800&seq=ads-hero-01&orientation=landscape';

const steps = [
  {
    icon: 'ri-file-list-3-line',
    title: '1. Choisissez votre offre',
    text: "Sélectionnez l'offre Starter, Business ou Premium selon vos objectifs de visibilité et votre budget.",
  },
  {
    icon: 'ri-upload-cloud-2-line',
    title: '2. Envoyez votre demande',
    text: "Renseignez les informations de votre entreprise et téléversez votre bannière publicitaire en quelques secondes.",
  },
  {
    icon: 'ri-team-line',
    title: '3. Lancement de la campagne',
    text: "Notre équipe examine votre demande, vous recontacte, puis votre publicité est diffusée sur les pages choisies.",
  },
];

const benefits = [
  {
    icon: 'ri-user-heart-line',
    title: 'Audience qualifiée',
    text: "Touchez une communauté active d'entreprises, de talents et de consommateurs en Afrique francophone.",
  },
  {
    icon: 'ri-pages-line',
    title: 'Visibilité multi-pages',
    text: "Diffusez votre bannière sur une page précise ou sur l'ensemble de la plateforme selon l'offre choisie.",
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Suivi de campagne',
    text: 'Chaque campagne est suivie par notre équipe du début à la fin, avec des emplacements définis et contrôlés.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Équipe dédiée',
    text: 'Un accompagnement personnalisé pour optimiser votre bannière et maximiser votre impact.',
  },
];

function AdsPage() {
  const [selectedOffer, setSelectedOffer] = useState<AdOffer | null>(null);

  const scrollToOffers = () => {
    document.getElementById('offres')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative min-h-[520px] md:min-h-[640px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={adsHero}
              alt="Publicité et sponsoring sur WorldApeiron"
              className="w-full h-full object-cover hero-drift"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background-50/80 via-background-50/60 to-background-50" />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50/90 via-background-50/40 to-transparent" />
          </div>

          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-20 pt-28">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-500/15 border border-accent-400/30 text-accent-400 text-xs font-semibold uppercase tracking-wider mb-5">
                <i className="ri-megaphone-line" />
                WorldApeiron Ads
              </span>
              <h1 className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl text-foreground-50 mb-5 leading-tight">
                Développez votre visibilité avec{' '}
                <span className="text-gradient-primary">WorldApeiron</span>
              </h1>
              <p className="text-base md:text-lg text-foreground-300 max-w-2xl leading-relaxed mb-8">
                Présentez votre entreprise à notre communauté grâce à nos solutions de publicité et
                de sponsoring. Choisissez l'offre qui correspond le mieux à vos objectifs.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={scrollToOffers}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-accent-500 text-foreground-950 font-semibold rounded-lg hover:bg-accent-400 transition-colors whitespace-nowrap text-sm"
                >
                  <i className="ri-sparkling-2-line" />
                  Découvrir nos offres
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedOffer(AD_OFFERS[2])}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-foreground-300/40 text-foreground-50 font-semibold rounded-lg hover:bg-foreground-50/10 transition-colors whitespace-nowrap text-sm"
                >
                  Devenir sponsor
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Offres */}
        <section id="offres" className="py-16 md:py-24 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <Reveal className="text-center mb-12">
              <span className="inline-block text-sm font-semibold text-accent-400 uppercase tracking-wider mb-3">
                Publicité & Sponsoring
              </span>
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-3">
                Nos offres publicitaires
              </h2>
              <p className="text-foreground-400 max-w-2xl mx-auto">
                Des formules simples et accessibles pour donner de la visibilité à votre marque sur
                la plateforme WorldApeiron.
              </p>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto pt-3">
              {AD_OFFERS.map((offer, i) => (
                <Reveal key={offer.key} delay={i * 120}>
                  <OfferCard offer={offer} onSubscribe={setSelectedOffer} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Pourquoi nous choisir */}
        <section className="py-16 md:py-24 bg-background-100 border-y border-background-200/70">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <Reveal className="text-center mb-12">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 mb-3">
                Pourquoi faire de la publicité avec WorldApeiron ?
              </h2>
              <p className="text-foreground-400 max-w-2xl mx-auto">
                Nous connectons votre entreprise à un écosystème digital complet, pensé pour
                l'Afrique francophone.
              </p>
            </Reveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
              {benefits.map((benefit, i) => (
                <Reveal key={benefit.title} delay={i * 100}>
                  <div className="h-full p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-400/40 transition-all duration-300">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-500/15 mb-4">
                      <i className={`${benefit.icon} text-2xl text-accent-400`} />
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-50 mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-foreground-400 leading-relaxed">{benefit.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Comment ça marche */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <Reveal className="text-center mb-12">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 mb-3">
                Comment ça marche ?
              </h2>
              <p className="text-foreground-400 max-w-2xl mx-auto">
                Lancer une campagne publicitaire sur WorldApeiron est simple et rapide.
              </p>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {steps.map((step, i) => (
                <Reveal key={step.title} delay={i * 120}>
                  <div className="relative h-full p-6 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="absolute top-6 right-6 text-5xl font-heading font-bold text-background-200/70 select-none">
                      {i + 1}
                    </div>
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500/10 mb-4">
                      <i className={`${step.icon} text-2xl text-primary-400`} />
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-50 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-foreground-400 leading-relaxed">{step.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <Reveal>
              <div className="relative overflow-hidden max-w-4xl mx-auto rounded-2xl bg-gradient-to-br from-primary-500/15 to-accent-500/10 border border-background-200/70 p-8 md:p-14 text-center">
                <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-accent-500/10 blur-2xl" />
                <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-primary-500/10 blur-2xl" />
                <div className="relative z-10">
                  <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-3">
                    Prêt à faire grandir votre marque ?
                  </h2>
                  <p className="text-foreground-400 mb-8 max-w-xl mx-auto">
                    Rejoignez les entreprises qui nous font déjà confiance et donnez un coup
                    d'accélérateur à votre visibilité dès aujourd'hui.
                  </p>
                  <button
                    type="button"
                    onClick={scrollToOffers}
                    className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 text-foreground-950 font-semibold rounded-lg hover:bg-accent-400 transition-colors whitespace-nowrap text-sm"
                  >
                    <i className="ri-megaphone-line" />
                    Souscrire maintenant
                  </button>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <Footer />

      <SubscribeModal offer={selectedOffer} onClose={() => setSelectedOffer(null)} />
    </div>
  );
}

export default AdsPage;